import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  DxDataGridModule,
  DxTextBoxModule,
  DxValidatorModule,
} from 'devextreme-angular';
import {
  MultiStepPopupComponent,
  Step,
} from 'src/app/components/multi-step-popup/multi-step-popup.component';
import { ContactsPopupComponent } from 'src/app/components/contacts-popup/contacts-popup.component';
import { GpdPillComponent } from 'src/app/components/gpd-pill/gpd-pill.component';
import { GpdRoleSelectComponent } from 'src/app/components/gpd-role-select/gpd-role-select.component';
import { GpdPaginationComponent } from 'src/app/components/gpd-pagination/gpd-pagination.component';
import { ContactService } from 'src/app/services/contact.service';
import { ConfigService } from 'src/app/services/config.service';
import { AppGlobals } from 'src/app/app.globals';
import { FeatureFlags } from 'src/app/interfaces/enums/feature-flag';
import { SocietyContact, SocietyService } from 'src/app/services/society.service';
import {
  ContactSearchService,
  ContactSearchSession,
  ContactSearchType,
  contactSearchLoadingText,
  contactSearchMessage,
} from 'src/app/services/contact-search.service';

interface SelectedContact {
  id: number;
  contactType: ContactSearchType;
  displayName: string;
  role: string;
}

@Component({
    selector: 'app-project-add-contact-modal',
    imports: [
        CommonModule,
        DxDataGridModule,
        DxTextBoxModule,
        DxValidatorModule,
        MultiStepPopupComponent,
        ContactsPopupComponent,
        GpdPillComponent,
        GpdRoleSelectComponent,
        GpdPaginationComponent,
    ],
    templateUrl: './project-add-contact-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./project-add-contact-modal.component.css']
})
export class ProjectAddContactModalComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() visible = false;
  @Input() projectId!: number;
  @Input() contactTags: any[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onSaved = new EventEmitter<void>();

  @ViewChild('selectStepTemplate') selectStepTemplate!: TemplateRef<any>;
  @ViewChild('roleStepTemplate') roleStepTemplate!: TemplateRef<any>;
  @ViewChild(MultiStepPopupComponent) multiStepPopup?: MultiStepPopupComponent;

  steps: Step[] = [];
  activeTab: ContactSearchType = 'company';

  // Captured from (onInitialized) — the grids live inside a dxTemplate, so
  // @ViewChild does not reach them.
  companyGridInstance: any;
  personGridInstance: any;

  companies: any[] = [];
  persons: any[] = [];

  selectedCompanyKeys: number[] = [];
  selectedPersonKeys: number[] = [];
  /** Gesellschaften: linked like on their own page, always with the role "Gesellschaft" (no role to pick). */
  selectedSocietyKeys: number[] = [];
  societies: SocietyContact[] = [];
  societyGridInstance: any;
  selectedContacts: SelectedContact[] = [];

  // Drives the hidden validator that gates step 1 until at least one contact is selected.
  selectionValidatorValue = '';

  // Company/person grid paging — standard (non-virtual) scrolling + DevExtreme's own paging
  // with its default pager hidden, driven instead by <app-gpd-pagination> below each grid,
  // same pattern as the "Add Component" popup (project-info-components.component). This
  // also means each step sizes to its actual content instead of forcing a fixed 82vh.
  companyGridCurrentPage = 1;
  companyGridTotalPages = 1;
  companyGridItemsPerPage = 0;
  companyGridTotalItems = 0;

  get companyGridShowingFrom(): number {
    if (this.companyGridTotalItems === 0) return 0;
    return this.companyGridItemsPerPage * (this.companyGridCurrentPage - 1) + 1;
  }

  get companyGridShowingTo(): number {
    return Math.min(this.companyGridItemsPerPage * this.companyGridCurrentPage, this.companyGridTotalItems);
  }

  personGridCurrentPage = 1;
  personGridTotalPages = 1;
  personGridItemsPerPage = 0;
  personGridTotalItems = 0;

  get personGridShowingFrom(): number {
    if (this.personGridTotalItems === 0) return 0;
    return this.personGridItemsPerPage * (this.personGridCurrentPage - 1) + 1;
  }

  get personGridShowingTo(): number {
    return Math.min(this.personGridItemsPerPage * this.personGridCurrentPage, this.personGridTotalItems);
  }

  /**
   * Each tab is searched on the server from 2 characters (sound-alikes, typos, titles, legal
   * forms, e-mail, city); its grid then shows exactly those hits in the server's order.
   * Shorter input filters the grid as before.
   */
  readonly searches: Record<ContactSearchType, ContactSearchSession>;
  searchText: Record<ContactSearchType, string> = { company: '', person: '', society: '' };
  readonly companyRank = (row: any): number => this.searches.company.rankOf('company', row?.id);
  readonly personRank = (row: any): number => this.searches.person.rankOf('person', row?.id);
  readonly societyRank = (row: any): number => this.searches.society.rankOf('society', row?.id);

  noDataText(type: ContactSearchType): string {
    return contactSearchMessage(this.searches[type]) ?? $localize`:@@noData:No data`;
  }

  get societiesEnabled(): boolean {
    return AppGlobals.UserCan(FeatureFlags.Gesellschaften);
  }

  /** Role shown (not editable) for a Gesellschaft in step 2. */
  readonly societyRole = $localize`:@@societyProjectRole:Gesellschaft`;

  societyGridCurrentPage = 1;
  societyGridTotalPages = 1;
  societyGridItemsPerPage = 0;
  societyGridTotalItems = 0;

  get societyGridShowingFrom(): number {
    if (this.societyGridTotalItems === 0) return 0;
    return this.societyGridItemsPerPage * (this.societyGridCurrentPage - 1) + 1;
  }

  get societyGridShowingTo(): number {
    return Math.min(this.societyGridItemsPerPage * this.societyGridCurrentPage, this.societyGridTotalItems);
  }

  addPopupVisible = false;
  addPopupType: 'company' | 'person' = 'company';

  private saving = false;
  private viewReady = false;

  constructor(
    private contactService: ContactService,
    private configService: ConfigService,
    private cdr: ChangeDetectorRef,
    contactSearchService: ContactSearchService,
    private societyService: SocietyService
  ) {
    this.searches = {
      company: contactSearchService.createSession(['company'], () => this.applySearch('company'), 50),
      person: contactSearchService.createSession(['person'], () => this.applySearch('person'), 50),
      society: contactSearchService.createSession(['society'], () => this.applySearch('society'), 50),
    };
  }

  private gridOf(type: ContactSearchType): any {
    return type === 'company' ? this.companyGridInstance : type === 'person' ? this.personGridInstance : this.societyGridInstance;
  }

  onSearchInput(type: ContactSearchType, value: string): void {
    this.searchText[type] = value ?? '';
    this.searches[type].setQuery(this.searchText[type]);
    // While the search runs the grid shows its loading panel; applySearch takes it away.
    if (this.searches[type].pending) this.gridOf(type)?.beginCustomLoading(contactSearchLoadingText());
    else this.applySearch(type);
  }

  /** Server hits while a search is active (no second client filter on top), otherwise the grid's own search. */
  private applySearch(type: ContactSearchType): void {
    const grid = this.gridOf(type);
    if (!grid) return;
    grid.endCustomLoading();
    const search = this.searches[type];
    if (search.active) {
      grid.searchByText('');
      grid.columnOption('searchRank', { sortOrder: 'asc', sortIndex: 0 });
      grid.filter((item: any) => search.isHit(type, item?.id));
    } else {
      grid.columnOption('searchRank', 'sortOrder', undefined);
      grid.filter(null);
      grid.searchByText(this.searchText[type].trim());
    }
    grid.pageIndex(0);
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    Object.values(this.searches).forEach(s => s.dispose());
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.buildSteps();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.reset();
      this.loadData();
      this.buildSteps();
    }
  }

  private buildSteps(): void {
    if (!this.viewReady || !this.selectStepTemplate || !this.roleStepTemplate) {
      return;
    }

    this.steps = [
      {
        id: 'select-contact',
        title: $localize`:@@selectContact:Select contact`,
        icon: 'user-multiple',
        template: this.selectStepTemplate,
      },
      {
        id: 'define-role',
        title: $localize`:@@defineRole:Define role`,
        icon: 'edit',
        template: this.roleStepTemplate,
      },
    ];

    this.cdr.detectChanges();
  }

  private reset(): void {
    this.activeTab = 'company';
    this.selectedCompanyKeys = [];
    this.selectedPersonKeys = [];
    this.selectedSocietyKeys = [];
    this.selectedContacts = [];
    this.selectionValidatorValue = '';
    this.saving = false;
    this.prefilledRoles.clear();
    for (const type of ['company', 'person', 'society'] as ContactSearchType[]) {
      this.searchText[type] = '';
      this.searches[type].setQuery('');
      this.applySearch(type);
    }
    // The popup content is not re-created between openings, so send it back to step 1.
    this.multiStepPopup?.reset();
  }

  private async loadData(): Promise<void> {
    try {
      const [companiesRes, personsRes] = await Promise.all([
        this.contactService.getCompanies({}),
        this.contactService.getPersons({}),
      ]);

      this.companies = companiesRes?.companyItems ?? [];
      this.persons = personsRes?.personItems ?? [];
      if (this.societiesEnabled) {
        this.societies = await this.societyService.loadContacts(true).catch(() => []);
      }

      if (!this.contactTags?.length) {
        this.contactTags = await this.configService.loadContactTags().catch(() => []);
      }
      this.cdr.detectChanges();
      // deferRendering is off, so the grids were created before the popup was
      // laid out and measured a zero-height viewport.
      setTimeout(() => {
        this.companyGridInstance?.updateDimensions();
        this.personGridInstance?.updateDimensions();
        this.societyGridInstance?.updateDimensions();
      });
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  setActiveTab(tab: ContactSearchType): void {
    this.activeTab = tab;
    // The inactive pane is display:none, so its grid measured a zero-height
    // viewport. Re-measure once it is laid out again.
    setTimeout(() => this.gridOf(tab)?.updateDimensions());
  }

  personName = (row: any): string => `${row?.firstname ?? ''} ${row?.lastname ?? ''}`.trim();

  getTag(id: number): string {
    const tag = this.contactTags?.find((t) => t.id === id);
    return tag?.title ?? '';
  }

  filterTags = (filterValue: any) => {
    if (!filterValue) {
      return null;
    }
    return (item: any) => Array.isArray(item?.tags) && item.tags.includes(filterValue);
  };

  onCompanyRowClick(e: any): void {
    this.toggleSelection(e);
  }

  onPersonRowClick(e: any): void {
    this.toggleSelection(e);
  }

  private toggleSelection(e: any): void {
    if (e.rowType !== 'data') {
      return;
    }
    // Ignore clicks that land on the checkbox itself — DevExtreme already toggles those.
    if (e.event?.target?.closest?.('.dx-command-select')) {
      return;
    }
    const grid = e.component;
    if (grid.isRowSelected(e.key)) {
      grid.deselectRows([e.key]);
    } else {
      grid.selectRows([e.key], true);
    }
  }

  onCompanySelectionChanged(e: any): void {
    this.selectedCompanyKeys = e.selectedRowKeys ?? [];
    this.updateSelectionValidator();
  }

  onPersonSelectionChanged(e: any): void {
    this.selectedPersonKeys = e.selectedRowKeys ?? [];
    this.updateSelectionValidator();
  }

  onSocietyRowClick(e: any): void {
    this.toggleSelection(e);
  }

  onSocietySelectionChanged(e: any): void {
    this.selectedSocietyKeys = e.selectedRowKeys ?? [];
    this.updateSelectionValidator();
  }

  getSocietyGridPageInfo(): void {
    if (!this.societyGridInstance) {
      return;
    }
    this.societyGridCurrentPage = this.societyGridInstance.pageIndex() + 1;
    this.societyGridTotalPages = this.societyGridInstance.pageCount();
    this.societyGridItemsPerPage = this.societyGridInstance.pageSize();
    this.societyGridTotalItems = this.societyGridInstance.totalCount();
  }

  onSocietyGridOptionChanged(e: any): void {
    if (e.fullName === 'paging.pageIndex' || e.fullName === 'paging.pageSize' || e.fullName === 'dataSource') {
      this.getSocietyGridPageInfo();
    }
  }

  onSocietyGridPageChange(page: number): void {
    if (this.societyGridInstance) {
      this.societyGridInstance.pageIndex(page - 1);
      this.getSocietyGridPageInfo();
    }
  }

  private updateSelectionValidator(): void {
    const count = this.selectedCompanyKeys.length + this.selectedPersonKeys.length + this.selectedSocietyKeys.length;
    this.selectionValidatorValue = count > 0 ? 'ok' : '';
    this.multiStepPopup?.triggerValidationUpdate();
  }

  getCompanyGridPageInfo(): void {
    if (!this.companyGridInstance) {
      return;
    }
    this.companyGridCurrentPage = this.companyGridInstance.pageIndex() + 1;
    this.companyGridTotalPages = this.companyGridInstance.pageCount();
    this.companyGridItemsPerPage = this.companyGridInstance.pageSize();
    this.companyGridTotalItems = this.companyGridInstance.totalCount();
  }

  onCompanyGridOptionChanged(e: any): void {
    if (e.fullName === 'paging.pageIndex' || e.fullName === 'paging.pageSize' || e.fullName === 'dataSource') {
      this.getCompanyGridPageInfo();
    }
  }

  onCompanyGridPageChange(page: number): void {
    if (this.companyGridInstance) {
      this.companyGridInstance.pageIndex(page - 1);
      this.getCompanyGridPageInfo();
    }
  }

  getPersonGridPageInfo(): void {
    if (!this.personGridInstance) {
      return;
    }
    this.personGridCurrentPage = this.personGridInstance.pageIndex() + 1;
    this.personGridTotalPages = this.personGridInstance.pageCount();
    this.personGridItemsPerPage = this.personGridInstance.pageSize();
    this.personGridTotalItems = this.personGridInstance.totalCount();
  }

  onPersonGridOptionChanged(e: any): void {
    if (e.fullName === 'paging.pageIndex' || e.fullName === 'paging.pageSize' || e.fullName === 'dataSource') {
      this.getPersonGridPageInfo();
    }
  }

  onPersonGridPageChange(page: number): void {
    if (this.personGridInstance) {
      this.personGridInstance.pageIndex(page - 1);
      this.getPersonGridPageInfo();
    }
  }

  onNextClicked(): void {
    this.syncSelectedContacts();
  }

  // Role picked while creating a brand-new contact inline (contacts-popup's showRole),
  // keyed the same way as previousRoles below — consulted as a fallback the first time that
  // contact gets synced into selectedContacts, so it shows up already filled in on step 2.
  private prefilledRoles = new Map<string, string>();

  private syncSelectedContacts(): void {
    const previousRoles = new Map(
      this.selectedContacts.map((c) => [`${c.contactType}:${c.id}`, c.role])
    );
    const result: SelectedContact[] = [];

    for (const id of this.selectedCompanyKeys) {
      const company = this.companies.find((c) => c.id === id);
      const key = `company:${id}`;
      result.push({
        id,
        contactType: 'company',
        displayName: company?.displayName || company?.name || '',
        role: previousRoles.get(key) ?? this.prefilledRoles.get(key) ?? '',
      });
    }
    for (const id of this.selectedPersonKeys) {
      const person = this.persons.find((p) => p.id === id);
      const key = `person:${id}`;
      result.push({
        id,
        contactType: 'person',
        displayName: person ? this.personName(person) : '',
        role: previousRoles.get(key) ?? this.prefilledRoles.get(key) ?? '',
      });
    }
    for (const id of this.selectedSocietyKeys) {
      const society = this.societies.find((s) => s.id === id);
      result.push({ id, contactType: 'society', displayName: society?.displayName ?? '', role: this.societyRole });
    }

    this.selectedContacts = result;
  }

  openAddNew(type: 'company' | 'person'): void {
    this.addPopupType = type;
    this.addPopupVisible = true;
  }

  onNewContactAdded(e: any): void {
    if (e.role) {
      this.prefilledRoles.set(`${e.type}:${e.id}`, e.role);
    }
    this.addContactToSelection(e.id, e.type, e.displayName);
    this.addPopupVisible = false;
  }

  onExistingSelectedFromPopup(e: any): void {
    const displayName =
      e.data?.displayName || `${e.data?.firstname ?? ''} ${e.data?.lastname ?? ''}`.trim();
    this.addContactToSelection(e.data?.id, e.type, displayName);
    this.addPopupVisible = false;
  }

  private addContactToSelection(id: number, type: 'company' | 'person', displayName: string): void {
    if (id == null) {
      return;
    }
    if (type === 'company') {
      if (!this.selectedCompanyKeys.includes(id)) {
        this.selectedCompanyKeys = [...this.selectedCompanyKeys, id];
      }
    } else {
      if (!this.selectedPersonKeys.includes(id)) {
        this.selectedPersonKeys = [...this.selectedPersonKeys, id];
      }
    }
    this.updateSelectionValidator();
    this.loadData();
  }

  // Committing the inline "add/rename role" field — via Enter or by clicking/tabbing
  // away — fires this (see gpd-role-select). If that was the last role this step needed,
  // finish the dialog right away instead of making the user follow up with a separate
  // click on "Add". Deferred a tick, same reasoning as the grid updateDimensions() calls
  // in loadData() above — gpd-role-select just swapped its *ngIf branch back to the
  // dx-select-box, and that freshly (re-)mounted widget needs to actually render before
  // its DevExtreme validator can be read.
  //
  // Trade-off: since this fires on any blur, clicking "Back" right after typing a role
  // also commits it and can trigger this same auto-finish if that was the last thing the
  // step needed — Back doesn't get a chance to run first. Accepted deliberately: the
  // common case (typing a role and moving straight to the submit button) matters more
  // than the rarer one (typing a role and immediately backing out instead).
  onRoleCommitted(): void {
    setTimeout(() => {
      this.multiStepPopup?.triggerAdvanceIfValid();
    });
  }

  async onFinishClicked(): Promise<void> {
    if (this.saving) {
      return;
    }
    this.saving = true;

    const payload = {
      ProjectId: this.projectId,
      Contacts: this.selectedContacts.map((c) => ({
        ContactId: c.id,
        ContactType: c.contactType,
        Role: c.role,
      })),
    };

    const result = await this.contactService
      .addProjectContact(payload)
      .catch((error) => ({ hasError: true, feedback: String(error) }));

    this.saving = false;

    if (result?.hasError) {
      AppGlobals.ShowErrorMessage(result.feedback);
      return;
    }

    this.onSaved.emit();
    this.close();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
